// Utilitário de logging seguro: mascara campos sensíveis antes de logar
const SENSITIVE_KEYS = [
  "name",
  "email",
  "phone",
  "message",
  "password",
  "token",
  "accessToken",
  "refreshToken",
  "cpf",
  "cnpj",
  "registration",
  "matricula",
  "ssn",
  "address",
];

const isObject = (v) => v && typeof v === "object" && !Array.isArray(v);

function shortHash(str) {
  // simples hash determinístico (djb2) para correlação — não criptográfico
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = (h * 33) ^ str.charCodeAt(i);
  }
  // converte para hex e reduz
  return (h >>> 0).toString(16).slice(-8);
}

function maskValue(key, value) {
  if (value == null) return value;
  if (typeof value === "string") {
    const lower = key.toLowerCase();
    // E-mail: manter domínio, mascarar local e adicionar hash
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && lower.includes("email")) {
      const parts = value.split("@");
      const local = parts[0];
      const domain = parts[1];
      return `${local.slice(0, 1)}***@${domain} (h:${shortHash(value)})`;
    }
    // token-like: mostrar prefix/suffix e hash
    if (lower.includes("token") || lower.includes("access") || lower.includes("refresh")) {
      const prefix = value.slice(0, 4);
      const suffix = value.slice(-4);
      return `${prefix}...${suffix} (h:${shortHash(value)})`;
    }
    // cpf/cnpj: máscara parcial
    if (lower.includes("cpf") || lower.includes("cnpj") || lower.includes("ssn")) {
      return `[REDACTED] (h:${shortHash(value)})`;
    }
  }
  return "[REDACTED]";
}

function maskObject(obj, depth = 0) {
  if (obj == null) return obj;
  if (depth > 5) return "[TRUNCATED]";
  if (Array.isArray(obj)) return obj.map((v) => (isObject(v) ? maskObject(v, depth + 1) : v));
  if (!isObject(obj)) return obj;
  const out = {};
  for (const key of Object.keys(obj)) {
    try {
      const lower = key.toLowerCase();
      const shouldMask = SENSITIVE_KEYS.some((k) => lower.includes(k.toLowerCase()));
      if (shouldMask) {
        out[key] = maskValue(key, obj[key]);
      } else if (isObject(obj[key]) || Array.isArray(obj[key])) {
        out[key] = maskObject(obj[key], depth + 1);
      } else {
        out[key] = obj[key];
      }
    } catch (e) {
      out[key] = "[ERROR_MASKING]";
    }
  }
  return out;
}

function sanitizeArg(arg) {
  if (isObject(arg) || Array.isArray(arg)) return maskObject(arg);
  // Se for string, tentar parsear JSON (requests/axios costumam serializar body)
  if (typeof arg === "string") {
    // tenta detectar JSON válido
    try {
      const parsed = JSON.parse(arg);
      return maskObject(parsed);
    } catch (e) {
      // não é JSON — tenta detectar e mascarar e-mails/tokens em strings
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(arg)) {
        return maskValue("email", arg);
      }
      // tokens muito longos podem ser parcialmente mascarados
      if (arg.length > 30 && /[A-Za-z0-9_-]{8,}/.test(arg)) {
        return maskValue("token", arg);
      }
      return arg;
    }
  }
  return arg;
}

const MASKING = process.env.REACT_APP_LOG_MASKING !== "false";

const safeLogger = {
  log: (...args) => {
    if (!MASKING) return console.log(...args);
    return console.log(...args.map(sanitizeArg));
  },
  info: (...args) => {
    if (!MASKING) return console.info(...args);
    return console.info(...args.map(sanitizeArg));
  },
  warn: (...args) => {
    if (!MASKING) return console.warn(...args);
    return console.warn(...args.map(sanitizeArg));
  },
  error: (...args) => {
    if (!MASKING) return console.error(...args);
    return console.error(...args.map(sanitizeArg));
  },
  // helper para mascarar explicitamente
  mask: (obj) => sanitizeArg(obj),
  hash: (s) => (typeof s === "string" ? shortHash(s) : null),
};

export default safeLogger;
